import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import axios from "axios";
import useAuth from "hooks/useAuth";
import useConstants from "constants";
import FullScreenLoader from "../components/FullScreenLoader";
import useLanguageSwitcher from "hooks/useLanguageSwitcher";
import useAPI from "hooks/useAPI";

const AuthMiddleware = ({ children }) => {
  const { ROUTES_WITHOUT_AUTH, AUTH_API_URL } = useConstants();
  const [isAuthenticated, setIsAuthenticated] = useState(null); // Track authentication status
  const location = useLocation();
  const { user, saveUser, BASE_URL, cleanUp, business, saveBusiness } = useAuth();
  const [loading, setLoading] = useState(true);
  const { switchLanguage } = useLanguageSwitcher();
  const { api, auth_api } = useAPI();
  if (ROUTES_WITHOUT_AUTH.includes(location.pathname)) {
    return children;
  }

  // Helper functions to manage tokens
  const getAccessToken = () => localStorage.getItem("__at_um");
  const getPermissions = () => sessionStorage.getItem("user_permissions");
  const getRefreshToken = () => localStorage.getItem("__rt_um");
  const saveTokens = (accessToken, refreshToken) => {
    localStorage.setItem("__at_um", accessToken);
    localStorage.setItem("__rt_um", refreshToken);
  };
  const savePermissions = (permissions) => {
    const permissions_stringify = JSON.stringify(permissions);
    sessionStorage.setItem("user_permissions", permissions_stringify);
  };
  const clearTokens = () => {
    localStorage.removeItem("__at_um");
    localStorage.removeItem("__rt_um");
  };

  // Validate and refresh token if necessary
  const validateToken = async () => {
    const accessToken = getAccessToken();
    if (!accessToken) {
      setIsAuthenticated(false);
      return;
    }

    try {
      // Validate the access token
      await axios.post(
        `${AUTH_API_URL}/authentication/verify/`,
        { platform: "kafka" },
        {
          withCredentials: true,
          headers: {
            "use-cookie-auth": "true",
          },
        }
      );
      setIsAuthenticated(true);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        // Token is invalid or expired, attempt to refresh
        const refreshToken = getRefreshToken();
        if (refreshToken) {
          try {
            const response = await axios.post(
              `${AUTH_API_URL}/authentication/refresh/`,
              { platform: "kafka" },
              {
                withCredentials: true,
                headers: {
                  "use-cookie-auth": "true",
                },
              }
            );
            saveTokens(response.data.access, response.data.refresh);
            saveUser(response.data.user_data);
            setIsAuthenticated(true);
          } catch (refreshError) {
            // Refresh failed, clear tokens and redirect to login
            clearTokens();
            setIsAuthenticated(false);
          }
        } else {
          // No refresh token available, redirect to login
          setIsAuthenticated(false);
        }
      } else {
        setIsAuthenticated(false);
      }
    }
  };

  useEffect(() => {
    const validateUserAsync = async () => {
      if (isAuthenticated) {
        await validateUser();
      }
    };
    validateUserAsync();
  }, [isAuthenticated]);

  const validateUser = async () => {
    if (!user) {
      try {
        const response = await axios.get(`${AUTH_API_URL}/authentication/user/`, {
          params: {},
          withCredentials: true, // Required to include cookies
        });

        if (response.status === 200) {
          saveUser(response.data);

          if (response.data?.lang) {
            switchLanguage(response.data.lang);
          } else {
            switchLanguage("en");
          }
        }
      } catch (error) {
        console.error("Error fetching user:", error?.response || error);
      }
    }
    setLoading(false);
  };

  const setAuthTokens = async () => {
    const accessToken = getAccessToken();

    if (!accessToken || !user) {
      try {
        const auth_response = await auth_api.get(`get-authentication/`, {
          params: {
            platform: "kafka",
          },
        });

        if (auth_response) {
          saveTokens(auth_response.data.auth_token, auth_response.data.refresh_token);
          saveUser(auth_response.data.user_data);
          if (auth_response.data.user_data?.lang) {
            switchLanguage(auth_response.data.user_data?.lang);
          } else {
            switchLanguage("en");
          }
        }
      } catch {
        setIsAuthenticated(false);
      }
    }
  };

  // Run token validation on mount
  useEffect(() => {
    const validateAuthentication = async () => {
      await setAuthTokens();
      await validateToken();
    };
    validateAuthentication();
  }, []);

  // Handle authentication state
  if (isAuthenticated === null) {
    return <FullScreenLoader />;
  }

  if (!isAuthenticated) {
    cleanUp();
  }

  if (loading) {
    return <FullScreenLoader />;
  }

  return children;
};

AuthMiddleware.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthMiddleware;
